import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a Kenyan invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Kenyan invoice should include: your business name and address; your PIN (Personal Identification Number) from KRA; the client name and address; a unique invoice number; the invoice date; a description of goods or services; the amount; and your payment details. VAT-registered businesses must also include their VAT registration number, the 16% VAT amount, and the VAT-inclusive total.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in Kenya?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The standard VAT rate in Kenya is 16%, which applies to most goods and services. Some supplies are zero-rated (0%) including exports, certain basic commodities, and agricultural inputs. Other supplies are VAT-exempt. VAT registration with KRA is mandatory for businesses with annual taxable turnover exceeding KES 5 million.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Kenyan freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Kenyan freelancers providing services to overseas clients typically invoice in USD. Export of services to non-residents is zero-rated for VAT — no 16% VAT is charged. Use the international invoice type to bill in USD, GBP, or EUR with your Kenyan bank details. Many Kenyan freelancers also receive payments through M-Pesa, Payoneer, or Wise.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do Kenyan businesses need to register for VAT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'VAT registration with KRA (Kenya Revenue Authority) is compulsory once your annual taxable turnover exceeds KES 5 million. Below this threshold, you are not required to register for VAT. However, all businesses operating in Kenya must obtain a PIN certificate from KRA for general tax compliance.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Kenyan users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Kenyan freelancers and businesses. Create professional invoices in USD, KES, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients in USD',
    features: ['USD, GBP, EUR billing', 'Zero-rated export format', 'SWIFT / bank details', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'red',
    btnClass: 'bg-red-600 hover:bg-red-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For local clients in KES',
    features: ['KES currency', 'Custom line items', 'Logo upload', 'PIN number field', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Percent,
    title: 'VAT Invoice',
    subtitle: 'For VAT-registered businesses',
    features: ['16% VAT auto-calculated', 'VAT registration number field', 'Net + VAT + total', 'KRA-ready format', 'KES currency'],
    to: '/create/vat',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorKenyaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Kenya: Free KES Invoice — M-Pesa, KRA eTIMS & 16% VAT | Invilic"
        description="Free invoice generator for Kenyan freelancers and businesses. KES and USD invoices, M-Pesa payment, KRA eTIMS, 16% VAT. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-kenya"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇰🇪 Kenya
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Kenya
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Kenyan freelancers and businesses. Create professional invoices in USD, KES, or any currency. Bill international clients with zero-rated export invoices or local clients in KES. Download a clean PDF in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/international" className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200">
              Bill International Clients <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create KES Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">International vs Local Invoice: Which Do You Need?</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-red-700 dark:text-red-400 mb-3">Use an International Invoice if:</div>
              {[
                'Your client is overseas (US, UK, Europe, Middle East, etc.)',
                'You bill in USD, GBP, EUR, or another foreign currency',
                'Your services are zero-rated for export — no 16% VAT applies',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a KES Invoice if:</div>
              {[
                'Your client is based in Kenya and pays in KES',
                'You provide services to local Kenyan businesses',
                'You need to include PIN and local business details',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* M-Pesa + KRA eTIMS section */}
        <section className="mb-16 grid sm:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl border border-green-100 dark:border-green-800/40 bg-green-50 dark:bg-green-900/20">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">M-Pesa Payments on Kenyan Invoices</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              For local Kenyan clients, M-Pesa is the most practical payment method. Add your payment details in the notes field:
            </p>
            <ul className="space-y-2 mb-0">
              {[
                'Personal: add your M-Pesa mobile number',
                'Business: use a Lipa na M-Pesa Paybill number',
                'Physical location: use an M-Pesa Till number',
                'Large amounts: include bank details too',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-red-100 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">KRA eTIMS — Electronic Tax Invoicing</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              eTIMS is KRA's electronic invoice system. Here is who needs it:
            </p>
            <ul className="space-y-2">
              {[
                'VAT-registered businesses (above KES 5M turnover) must use eTIMS',
                'Freelancers below the VAT threshold: standard invoices are fine',
                'Register at etims.kra.go.ke using your KRA PIN',
                'Export services to non-residents: zero-rated, no 16% VAT applies',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <CheckCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Kenya Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Kenyan Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF in USD, KES, or any currency. No account required.</p>
          <Link to="/create/international" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
